/**
 * The current event of a device during a show (decision of Nov 3, 2026):
 * sellers captured at Ecomondo without having picked it. During a show
 * (between its dates) whose team the seller is on, the device picks it by
 * itself; with several such shows, or none picked, capture asks first.
 */

type EventLike = Readonly<Record<string, unknown>>;

const day = (v: unknown) => (typeof v === 'string' && v ? v.slice(0, 10) : null);

/**
 * The shows on today: active (not wrapped up), deleted excluded, `today`
 * (the device's date, YYYY-MM-DD) between their first and last day. An
 * event without dates is never "on today".
 */
export function showsToday<T extends EventLike>(events: readonly T[], today: string): T[] {
  return events.filter((e) => {
    if (e.deleted || e.status === 'completed') return false;
    const start = day(e.starts_on);
    const end = day(e.ends_on) ?? start;
    if (!start || !end) return false;
    return start <= today && today <= end;
  });
}

/**
 * The shows on today whose team the person is on (`on_my_team`, set in
 * Travel > Team going or Events > Edit): only they pick the event by
 * themselves or ask for it; someone at the office is never asked.
 */
export function myShowsToday<T extends EventLike>(events: readonly T[], today: string): T[] {
  return showsToday(events, today).filter((e) => e.on_my_team === true);
}

/** The show the device picks by itself: the one show of the person's team on today, else null. */
export function autoCurrentEvent<T extends EventLike>(
  events: readonly T[],
  today: string,
): T | null {
  const mine = myShowsToday(events, today);
  return mine.length === 1 ? mine[0]! : null;
}

/** Today where the code runs (the device), as YYYY-MM-DD. */
export function deviceToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
